import { forwardRef, useCallback, useEffect, useRef, useState } from 'react'
import { isSameDate } from 'components/ui/DatePicker/utils'
import dayjs from 'dayjs'
import { useTranslation } from 'react-i18next'
import useControllableState from 'components/ui/hooks/useControllableState'
import useMergedRef from 'components/ui/hooks/useMergeRef'
import capitalize from 'components/ui/utils/capitalize'
import Calendar from 'components/ui/DatePicker/Calendar'
import BasePicker from 'components/ui/DatePicker/BasePicker'
import Button from 'components/ui/Buttons'
import Input from 'components/ui/Input'
import CloseButton from 'components/ui/CloseButton'
import { Drawer } from 'components/ui'
import { useConfig } from 'components/ui/ConfigProvider'
import { HiOutlineCalendar } from 'react-icons/hi'
import useResponsive from 'utils/hooks/useResponsive'
import WheelTimePicker from './WheelTimePicker'

const DEFAULT_INPUT_FORMAT = 'DD-MMM-YYYY HH:mm'

const ScheduleDateTimePicker = forwardRef((props, ref) => {
    const {
        className,
        clearable,
        clearButtonLabel,
        defaultMonth,
        defaultOpen,
        defaultValue,
        disabled,
        disableDate,
        firstDayOfWeek,
        inputFormat,
        inputPrefix,
        inputSuffix,
        locale,
        maxDate,
        minDate,
        minDateTime,
        name,
        okButtonContent,
        onBlur,
        onChange,
        onFocus,
        onDropdownClose,
        onDropdownOpen,
        onOk,
        openPickerOnClear,
        pickerTitle,
        placeholder,
        size,
        value,
        ...rest
    } = props

    const { t } = useTranslation()
    const { locale: themeLocale } = useConfig()
    const { smaller } = useResponsive()
    const isMobile = smaller.md
    const finalLocale = locale || themeLocale
    const dateFormat = inputFormat || DEFAULT_INPUT_FORMAT

    const [dropdownOpened, setDropdownOpened] = useState(defaultOpen)
    const [mobileStep, setMobileStep] = useState('date')
    const [timeError, setTimeError] = useState(null)
    const inputRef = useRef()
    const mergedRef = useMergedRef(ref, inputRef)

    const [, setLastValidValue] = useState(defaultValue ?? null)
    const [_value, setValue] = useControllableState({
        prop: value,
        defaultProp: defaultValue,
        onChange,
    })

    const [focused, setFocused] = useState(false)
    const [inputState, setInputState] = useState(
        _value instanceof Date
            ? capitalize(dayjs(_value).locale(finalLocale).format(dateFormat))
            : ''
    )

    const formatInput = (date) =>
        capitalize(dayjs(date).locale(finalLocale).format(dateFormat))

    const closeDropdown = () => {
        setDropdownOpened(false)
        onDropdownClose?.()
    }

    const openDropdown = () => {
        setDropdownOpened(true)
        setMobileStep('date')
        onDropdownOpen?.()
    }

    useEffect(() => {
        if (value === null && !focused) setInputState('')
        if (value instanceof Date && !focused) setInputState(formatInput(value))
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, focused])

    const handleDateChange = (date) => {
        if (_value instanceof Date) {
            date.setHours(_value.getHours())
            date.setMinutes(_value.getMinutes())
        } else {
            const now = new Date()
            now.setHours(now.getHours() + 1)
            date.setHours(now.getHours())
            date.setMinutes(now.getMinutes())
        }
        setValue(date)
        setInputState(formatInput(date))
        if (timeError) setTimeError(null)
    }

    const handleTimeChange = (updatedDate) => {
        const dt = new Date(
            _value.getFullYear(),
            _value.getMonth(),
            _value.getDate(),
            updatedDate.getHours(),
            updatedDate.getMinutes(),
            0
        )
        setValue(dt)
        setInputState(formatInput(dt))
        if (timeError) setTimeError(null)
    }

    const handleClear = () => {
        setValue(null)
        setLastValidValue(null)
        setInputState('')
        openPickerOnClear && openDropdown()
        inputRef.current?.focus()
        onChange?.(null)
    }

    const today = useRef(new Date())

    const renderDay = useCallback((date) => {
        if (isSameDate(date, today.current)) {
            return <span title="Hoy">{date.getDate()}</span>
        }
        return date.getDate()
    }, [])

    const [okLoading, setOkLoading] = useState(false)

    const handleOk = async () => {
        if (minDateTime && _value instanceof Date && _value <= minDateTime) {
            const minTime = dayjs(minDateTime).format('HH:mm')
            setTimeError(
                t('schedule_time_after', {
                    defaultValue: 'La hora debe ser posterior a las {{time}}',
                    time: minTime,
                })
            )
            return
        }
        setTimeError(null)

        if (onOk) {
            setOkLoading(true)
            try {
                await onOk(_value)
            } catch (err) {
                setTimeError(err?.message || String(err))
                setOkLoading(false)
                return
            }
            setOkLoading(false)
        }

        setInputState(formatInput(_value))
        closeDropdown()
        window.setTimeout(() => inputRef.current?.focus(), 0)
        onChange?.(_value)
    }

    const pickerContent = (
        <>
            <Calendar
                locale={finalLocale}
                defaultMonth={
                    defaultMonth || (_value instanceof Date ? _value : new Date())
                }
                value={_value instanceof Date ? _value : (_value && dayjs(_value).toDate())}
                onChange={handleDateChange}
                minDate={minDate}
                maxDate={maxDate}
                disableDate={disableDate}
                firstDayOfWeek={firstDayOfWeek}
                preventFocus={false}
                renderDay={renderDay}
            />
            <div className="border-t border-gray-200 dark:border-gray-600 pt-2 pb-1 px-2">
                <div className="flex items-center justify-between">
                    <WheelTimePicker
                        value={_value}
                        onChange={handleTimeChange}
                        disabled={!(_value instanceof Date)}
                    />
                    <Button
                        size="sm"
                        variant="solid"
                        onClick={handleOk}
                        loading={okLoading}
                        disabled={okLoading || !(_value instanceof Date)}
                    >
                        {okButtonContent}
                    </Button>
                </div>
                {timeError && (
                    <p className="text-xs text-red-500 mt-1 px-1">{timeError}</p>
                )}
            </div>
        </>
    )

    if (isMobile) {
        const showClear = clearable && !!_value && !disabled
        const handleDateSelect = (date) => {
            handleDateChange(date)
            setMobileStep('time')
        }
        return (
            <>
                <Input
                    ref={mergedRef}
                    className={className}
                    placeholder={placeholder}
                    size={size}
                    name={name}
                    value={inputState}
                    readOnly
                    suffix={showClear
                        ? <div onClick={handleClear}>{clearButtonLabel || <CloseButton className="text-base" />}</div>
                        : (inputSuffix || <HiOutlineCalendar className="text-lg" />)
                    }
                    prefix={inputPrefix}
                    onClick={() => !disabled && openDropdown()}
                    autoComplete="off"
                    disabled={disabled}
                />
                <Drawer
                    isOpen={dropdownOpened}
                    onClose={closeDropdown}
                    onRequestClose={closeDropdown}
                    placement="bottom"
                    height={420}
                    showBackdrop
                    closable={false}
                    bodyClass="p-0 flex flex-col"
                >
                    <div className="px-5 pt-4 pb-2 flex items-center justify-between border-b border-gray-200 dark:border-gray-700">
                        <span className="text-base font-semibold text-gray-800 dark:text-gray-200">
                            {pickerTitle || placeholder || t('date_and_time', { defaultValue: 'Fecha y hora' })}
                        </span>
                        <CloseButton onClick={closeDropdown} />
                    </div>

                    <div className="flex-1 overflow-hidden flex flex-col">
                        <div
                            className="flex flex-1 min-h-0"
                            style={{
                                width: '200%',
                                transform: `translateX(${mobileStep === 'time' ? '-50%' : '0'})`,
                                transition: 'transform 0.25s ease-out',
                            }}
                        >
                            {/* Date step */}
                            <div className="w-1/2 overflow-y-auto px-4 pt-3 pb-2 flex flex-col items-center">
                                <Calendar
                                    locale={finalLocale}
                                    defaultMonth={
                                        defaultMonth || (_value instanceof Date ? _value : new Date())
                                    }
                                    value={_value instanceof Date ? _value : (_value && dayjs(_value).toDate())}
                                    onChange={handleDateSelect}
                                    minDate={minDate}
                                    maxDate={maxDate}
                                    disableDate={disableDate}
                                    firstDayOfWeek={firstDayOfWeek}
                                    preventFocus={false}
                                    renderDay={renderDay}
                                />
                            </div>
                            {/* Time step */}
                            <div className="w-1/2 flex flex-col">
                                <div className="flex-1 px-4 pt-3 pb-2 flex flex-col items-center justify-center">
                                    {_value instanceof Date && (
                                        <p className="text-sm text-gray-500 dark:text-gray-400 mb-3">
                                            {capitalize(dayjs(_value).locale(finalLocale).format('dddd, D [de] MMMM'))}
                                        </p>
                                    )}
                                    <WheelTimePicker
                                        mobile
                                        value={_value}
                                        onChange={handleTimeChange}
                                        disabled={!(_value instanceof Date)}
                                    />
                                    {timeError && (
                                        <p className="text-xs text-red-500 mt-2">{timeError}</p>
                                    )}
                                </div>
                                <div className="px-5 py-3 border-t border-gray-200 dark:border-gray-700 flex gap-3">
                                    <Button
                                        block
                                        variant="plain"
                                        onClick={() => setMobileStep('date')}
                                    >
                                        {t('back', { defaultValue: 'Atrás' })}
                                    </Button>
                                    <Button
                                        block
                                        variant="solid"
                                        onClick={handleOk}
                                        loading={okLoading}
                                        disabled={okLoading || !(_value instanceof Date)}
                                    >
                                        {okButtonContent}
                                    </Button>
                                </div>
                            </div>
                        </div>
                    </div>
                </Drawer>
            </>
        )
    }

    return (
        <BasePicker
            dropdownOpened={dropdownOpened}
            setDropdownOpened={setDropdownOpened}
            onDropdownClose={onDropdownClose}
            onDropdownOpen={onDropdownOpen}
            ref={mergedRef}
            className={className}
            onBlur={(e) => { onBlur?.(e); setFocused(false) }}
            onFocus={(e) => { onFocus?.(e); setFocused(true) }}
            name={name}
            inputLabel={inputState}
            clearable={clearable && !!_value && !disabled}
            clearButtonLabel={clearButtonLabel}
            onClear={handleClear}
            disabled={disabled}
            size={size}
            inputPrefix={inputPrefix}
            inputSuffix={inputSuffix}
            placeholder={placeholder}
            placement={rest.placement}
            popperStrategy={rest.popperStrategy}
        >
            {pickerContent}
        </BasePicker>
    )
})

ScheduleDateTimePicker.defaultProps = {
    defaultOpen: false,
    name: 'dateTime',
    clearable: true,
    disabled: false,
    firstDayOfWeek: 'monday',
    okButtonContent: 'OK',
}

export default ScheduleDateTimePicker
